import { type ChangeEvent, useState, type ReactNode  } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Stack } from '@mui/material';

import { partnerLinkCreate } from '@/entities/partner';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';
import { TextField } from '@/shared/ui/lib/TextField';

interface CreateLinkDialogProps {
  open: boolean;
  onClose: () => void;
}

export function CreateLinkDialog({ open, onClose }: CreateLinkDialogProps): ReactNode {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState('');

  const create = useMutation({
    ...partnerLinkCreate(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['partner', 'links'] });
      setName('');
      onClose();
    },
  });

  const isValid = name.trim().length > 0 && name.trim().length <= 120;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('common:affiliatePage.dialogs.createLink.title') ?? ''}
      leftButtonSlot={
        <Button onClick={onClose} disabled={create.isPending}>
          {t('common:buttons.cancel')}
        </Button>
      }
      rightButtonSlot={
        <Button
          variant="contained"
          disabled={!isValid || create.isPending}
          onClick={() => create.mutate({ name: name.trim() })}
        >
          {t('common:buttons.add')}
        </Button>
      }
    >
      <Stack spacing={2} sx={{ minWidth: 360 }}>
        <TextField
          label={t('common:affiliatePage.dialogs.createLink.nameLabel')}
          placeholder={t('common:affiliatePage.linkNamePlaceholder') ?? ''}
          value={name}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          fullWidth
          autoFocus
        />
      </Stack>
    </Dialog>
  );
}
